/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { ActionStatus } from "../actions.interfaces";
import { ServerError } from "../dto/error.interfaces";
import { Organization } from "../dto/organization.interfaces";
import {
    InvitedCloudV2TrialUser,
    OnboardCloudV2TrialUserResponse,
    PlatformUser,
} from "../dto/user.interfaces";
import {
    GetInvitedCloudV2TrialUsers,
    GetUser,
    GetUserOrganizations,
    InviteCloudV2TrialUser,
    OnboardCloudV2TrialUser,
} from "./user.interfaces";
import {
    getInvitedCloudV2TrialUsers as getInvitedCloudV2TrialUsersREST,
    getUser as getUserREST,
    getUserOrganizations as getUserOrganizationsREST,
    inviteCloudV2TrialUser as inviteCloudV2TrialUserREST,
    onboardCloudV2TrialUser as onboardCloudV2TrialUserREST,
} from "./user.rest";

export const useGetUserOrganizations = (): GetUserOrganizations => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [status, setStatus] = useState(ActionStatus.Initial);
    const [error, setError] = useState<string | ServerError[]>("");

    const getUserOrganizations = useCallback(async () => {
        setStatus(ActionStatus.Working);
        try {
            const fetchedOrganizations = await getUserOrganizationsREST();
            setOrganizations(fetchedOrganizations);
            setError("");
            setStatus(ActionStatus.Done);
        } catch (error) {
            const axiosError = error as AxiosError;
            setOrganizations([]);
            setError(
                axiosError &&
                    axiosError.response &&
                    axiosError.response.data &&
                    (axiosError.response.data.error ||
                        axiosError.response.data.errors ||
                        "")
            );
            setStatus(ActionStatus.Error);
        }
    }, []);

    return {
        organizations,
        getUserOrganizations,
        status,
        error,
    };
};

export const useGetUser = (): GetUser => {
    const [user, setUser] = useState<PlatformUser | null>(null);
    const [status, setStatus] = useState(ActionStatus.Initial);
    const [error, setError] = useState<string | ServerError[]>("");

    const getUser = useCallback(async () => {
        setStatus(ActionStatus.Working);
        try {
            const fetchedUser = await getUserREST();
            setUser(fetchedUser);
            setError("");
            setStatus(ActionStatus.Done);
        } catch (error) {
            const axiosError = error as AxiosError;
            setUser(null);
            setError(
                axiosError &&
                    axiosError.response &&
                    axiosError.response.data &&
                    (axiosError.response.data.error ||
                        axiosError.response.data.errors ||
                        "")
            );
            setStatus(ActionStatus.Error);
        }
    }, []);

    return {
        user,
        getUser,
        status,
        error,
    };
};

export const useOnboardCloudV2TrialUser = (): OnboardCloudV2TrialUser => {
    const [response, setResponse] =
        useState<OnboardCloudV2TrialUserResponse | null>(null);
    const [status, setStatus] = useState(ActionStatus.Initial);
    const [error, setError] = useState<string | ServerError[]>("");

    const onboardCloudV2TrialUser = useCallback(async () => {
        setStatus(ActionStatus.Working);
        try {
            const fetchedResponse = await onboardCloudV2TrialUserREST();
            setResponse(fetchedResponse);
            setError("");
            setStatus(ActionStatus.Done);
        } catch (error) {
            const axiosError = error as AxiosError;
            setResponse(null);
            setError(
                axiosError &&
                    axiosError.response &&
                    axiosError.response.data &&
                    (axiosError.response.data.error ||
                        axiosError.response.data.errors ||
                        "")
            );
            setStatus(ActionStatus.Error);
        }
    }, []);

    return {
        response,
        onboardCloudV2TrialUser,
        status,
        error,
    };
};

export const useInviteCloudV2TrialUser = (): InviteCloudV2TrialUser => {
    const [status, setStatus] = useState(ActionStatus.Initial);
    const [error, setError] = useState<string>("");

    const inviteCloudV2TrialUser = useCallback(async (email: string) => {
        setStatus(ActionStatus.Working);
        try {
            await inviteCloudV2TrialUserREST(email);
            setError("");
            setStatus(ActionStatus.Done);
        } catch (error) {
            const axiosError = error as AxiosError;
            setError(
                axiosError &&
                    axiosError.response &&
                    axiosError.response.data &&
                    (axiosError.response.data.message || "")
            );
            setStatus(ActionStatus.Error);
        }
    }, []);

    return {
        inviteCloudV2TrialUser,
        status,
        error,
    };
};

export const useGetInvitedCloudV2TrialUsers =
    (): GetInvitedCloudV2TrialUsers => {
        const [response, setResponse] = useState<
            InvitedCloudV2TrialUser[] | null
        >(null);
        const [status, setStatus] = useState(ActionStatus.Initial);
        const [error, setError] = useState<string | ServerError[]>("");

        const getInvitedCloudV2TrialUsers = useCallback(async () => {
            setStatus(ActionStatus.Working);
            try {
                const fetchedResponse = await getInvitedCloudV2TrialUsersREST();
                setResponse(fetchedResponse);
                setError("");
                setStatus(ActionStatus.Done);
            } catch (error) {
                setResponse(null);
                const axiosError = error as AxiosError;
                setError(
                    axiosError &&
                        axiosError.response &&
                        axiosError.response.data &&
                        (axiosError.response.data.error ||
                            axiosError.response.data.errors ||
                            "")
                );
                setStatus(ActionStatus.Error);
            }
        }, []);

        return {
            getInvitedCloudV2TrialUsers,
            invitedUsers: response,
            status,
            error,
        };
    };
